import { getContent } from '../../../shared/content.js';
import { plainText } from '../../../shared/content-types.js';
import type { Locale } from '../../../shared/locales.js';
import { RichText } from '../../components/RichText.js';
import { useLocale } from '../../i18n.js';
import { AdSlot } from '../AdSlot.js';

/**
 * FAQ.
 *
 * Long-tail questions and trust. No financial advice, no claims about what anyone can
 * afford, and honest answers to the awkward ones ("can someone cheat?", "can you see my
 * number?").
 *
 * `faqEntries` gives the prerenderer the same questions in plain text so it can emit
 * matching FAQPage structured data. Structured data that is generated from the rendered
 * copy cannot drift away from it, which is the only condition under which emitting it is
 * honest.
 */

export interface FaqEntry {
  question: string;
  /** Plain-text answer for structured data. */
  text: string;
}

export function faqEntries(locale: Locale): FaqEntry[] {
  return getContent(locale).faq.items.map((item) => ({
    question: item.q,
    text: item.a.map(plainText).join(' '),
  }));
}

export function Faq() {
  const c = getContent(useLocale()).faq;

  return (
    <>
      <section className="section">
        <div className="section__inner section__inner--narrow">
          <h1 className="section__title">{c.heading}</h1>
          <p className="section__lede">{c.lede}</p>

          <div className="faq-list">
            {c.items.map((item) => (
              <article className="faq-item" key={item.q}>
                <h2 className="faq-item__question">{item.q}</h2>
                <div className="faq-item__answer prose">
                  {item.a.map((paragraph) => (
                    <p key={paragraph.slice(0, 24)}>
                      <RichText text={paragraph} />
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <AdSlot slot="faq-footer" />
    </>
  );
}
